import {CONDITION_OPERATORS, IConditionFieldPreset} from "../../../policy-types";
import {
    ICheckboxesTreeItem
} from "../../../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store";
import {makeAutoObservable} from "mobx";
import LocationSelectionConverter from "./location-selection-converter";
import {ILocationLabelsGroups} from "./location-labels-grouper";
import {ICompareValueStore} from "../compare-value-factory";
import React from "react";
import LocationCompareValueList
    from "../../../components/policy-editor/policy-eligibility-card/condition-group-card/condition-builder/condition-field/compare-value-type-fields/location-compare-value-list/location-compare-value-list";
import {
    ICompareValueTypeComponentProps
} from "../../../components/policy-editor/policy-eligibility-card/condition-group-card/condition-builder/condition-field/condition-field";
import {isSet} from "../../../../../models/model-types-methods";
import {rootContainer, TYPE} from "../../../../../services/container/root";
import ErrorReportingService from "../../../../../services/error-reporting.service";
import type {ILocationCompareValueList} from "./location-selection-types";

export type {
    ILocationToken,
    ILocationUiToken,
    ILocationPersistenceToken,
    ILocationCompareValueList,
} from "./location-selection-types";

/**
 * LocationCompareValueStoreList - orchestrates location comparison state.
 * Manages model/changes/uncommitted buffers and delegates transforms to LocationSelectionConverter.
 */
class LocationCompareValueStoreList implements ICompareValueStore<ILocationCompareValueList> {

    private readonly _model: ILocationCompareValueList;
    private readonly _selectionConverter: LocationSelectionConverter;

    private _changes: Partial<ILocationCompareValueList> = {};
    private _uncommittedChanges: Partial<ILocationCompareValueList> = {};

    /** Initializes store with field preset and optional compare value from DB. */
    constructor(private _fieldPreset: IConditionFieldPreset, compareValue?: string) {
        makeAutoObservable(this, {}, {autoBind: true});
        this._selectionConverter = new LocationSelectionConverter(this._fieldPreset);
        this._model = this._deserializeModel(compareValue);
    }

    private get errorReportingService(): ErrorReportingService {
        return rootContainer.get<ErrorReportingService>(TYPE.ErrorReportingService);
    }

    /** Handles location selection changes from UI. Extracts tokens and stores as uncommitted changes. */
    public onLocationsChanged = (items: ICheckboxesTreeItem[] | undefined) => {
        const tokens = this._selectionConverter.fromTree(items);
        this._uncommittedChanges = {_ver: 2, list: tokens};
    }

    readonly Component: React.FC<ICompareValueTypeComponentProps> = LocationCompareValueList;

    /** Commits uncommitted UI state into committed changes. Clears if equals model. */
    commitChanges(): void {
        const commit: Partial<ILocationCompareValueList> = {
            ...this._changes,
            ...this._uncommittedChanges
        };

        /** No changes to commit */
        if (Object.keys(commit).length === 0) {

            /**
             * Could happen if commitChanges was triggered for a logical operator,
             * in this case there will be no uncommitedChanges or changes in the list itself.
             */
            return;
        }

        /** Commit equals model - no actual change */
        if (this._areCompareListsEqual(commit, this._model)) {
            this._changes = {};
            this._uncommittedChanges = {};
            return;
        }

        /** Store committed changes */
        this._changes = this._toPersistedModel(commit as ILocationCompareValueList);
        this._uncommittedChanges = {};
    }

    /** Discards uncommitted UI state. */
    discardUncommittedChanges(): void {
        this._uncommittedChanges = {};
    }

    /** Returns merged snapshot: model + committed changes + uncommitted changes. */
    getUncommittedChanges(): ILocationCompareValueList {
        return {
            ...this._model,
            ...this._changes,
            ...this._uncommittedChanges
        };
    }

    /** No-op for location comparison. */
    onOperatorChanged(op: CONDITION_OPERATORS): void {
    }

    /** Sets committed changes from external payload (e.g. undo/redo). Clears if equals model. */
    setChanges(changes: string | undefined): void {
        if (!changes) {
            this._changes = {};
            return;
        }

        this._changes = this._deserializeModel(changes);

        /**
         * If an external value equals the committed model – clear changes to avoid false positives.
         */
        if (this._areCompareListsEqual(this._changes as ILocationCompareValueList, this._model)) {
            this._changes = {};
            return;
        }
    }

    /** Discards committed changes. */
    discardChanges(): void {
        this._changes = {};
    }

    /** Returns serialized model to save to DB. Uses committed changes if present. */
    getUpdatedModel(): string | undefined {
        const source = this.hasChanges()
            ? {...this._model, ...this._changes}
            : this._model;

        return JSON.stringify(this._toPersistedModel(source));
    }

    /** Checks if there are any committed changes. */
    hasChanges(): boolean {
        return Object.keys(this._changes).length > 0;
    }

    /** Deserializes compareValue from DB to version 2 persistence format. */
    private _deserializeModel(compareValue?: string): ILocationCompareValueList {
        if (!isSet(compareValue)) {
            return {_ver: 2, list: undefined};
        }

        let parsed;

        try {
            parsed = JSON.parse(compareValue);
        }
        catch (error) {
            this.errorReportingService.captureException(
                error as Error,
                { compareValue, component: 'LocationCompareValueStoreList' }
            );
            console.error('[LocationCompareValueStoreList] Not valid json parsed. Error:', compareValue, error);
            return {_ver: 2, list: undefined};
        }

        return this._selectionConverter.normalizeCompareValue(parsed);
    }

    /** Normalizes tokens for DB persistence. */
    private _toPersistedModel(data: ILocationCompareValueList): ILocationCompareValueList {
        return {
            _ver: 2,
            list: data.list
                ? this._selectionConverter.toPersistence(data.list)
                : undefined,
        };
    }

    /**
     * Compares lists by token set equality (order-independent).
     * Critical: undefined and [] are different states (no selection vs select all).
     */
    private _areCompareListsEqual(
        updated: Partial<ILocationCompareValueList>,
        model: ILocationCompareValueList
    ): boolean {
        return this._selectionConverter.areTokenListsEqual(updated.list, model.list);
    }

    /** Returns full options tree for dropdown. */
    public getOptionsTree(): ICheckboxesTreeItem[] {
        return this._selectionConverter.getOptionsTree();
    }

    /** Returns selected value tree for dropdown. Computed on-demand from current state. */
    public getValueTree(): ICheckboxesTreeItem[] | undefined {
        return this._selectionConverter.toTree(this.getUncommittedChanges().list);
    }

    /** Returns grouped labels for rendering tags. */
    public getGroupedLabels(): ILocationLabelsGroups {
        return this._selectionConverter.buildGroupedLabels(this.getUncommittedChanges().list);
    }
}

export default LocationCompareValueStoreList;
